import { push } from 'redux-first-router'

import { basicFields as agentFields } from 'api/agents'
import { basicFields as groupFields } from 'api/groups'
import { basicFields as folderFields } from 'api/folders'

import graphql from 'api/graphql'
import grooveAPI from 'api/groove'
import { doGraphqlRequest } from 'ducks/requests'
import { basicFields as accessListFields } from 'api/access_lists'
import { basicFields as accountFields } from 'api/account'
import { basicFields as integrationFields } from 'api/integrations'
import { basicFields as userFields } from 'ducks/currentUser/api'
import { basicFields as companyFields } from 'api/company'
import { selectCurrentUserHasFullAccess } from 'ducks/currentUser/selectors/base'
import { basicFields as mailboxFields } from 'ducks/mailboxes/api'

import { doBootstrapWidgetsAndSettings } from 'ducks/widgets/operations'

import {
  basicFields as billingFields,
  BILLING_PLANS_FIELDS,
  CREDIT_CARD_FIELDS,
} from 'ducks/billing/api'
import { APP_BILLING_EXPIRED } from 'ducks/billing/types'

import * as types from 'constants/action_types'
import * as pages from 'constants/pages'

import { doFetchInaccessibleMailboxes } from 'actions/mailboxes'
import { doOpenFolderPage, doOpenSearchPage } from 'actions/pages'
import {
  doFetchTicketAggregationCountsForUnloadedNavigationSections,
  doFetchOpenTicketAggregationCountsForEachMailbox,
  doFetchTicketSearchCountsForPinnedSearches,
  doFetchTicketSearchCountsFor,
  doLoadSearchTickets,
} from 'actions/search'

import {
  isBoostrappedSelector,
  selectIsInboxBootstrapped,
  currentPathSelector,
  oauthTokenSelector,
  selectReturnPath,
  selectTicketListBeforeSearch,
  fetchingStatusesSelector,
  selectCurrentTicketList,
  selectAccountIsOnboardingCompleted,
  isAuthenticatedSelector,
} from 'selectors/app'
import {
  selectQueryParams,
  selectCurrentPage,
  selectIsInOnboarding,
} from 'selectors/location'
import { selectCurrentSearchPin } from 'selectors/pinned_searches'
import { selectAllFolderQueries } from 'selectors/folders'
import { selectedPinnedSearchesList } from 'selectors/left_nav'
import {
  selectTicketSearchOperatorValueMap,
  selectCurrentTicketSearchQueryId,
  selectCurrentTicketSearchQueryObject,
  selectCurrentTicketSearchResultTicketIds,
} from 'selectors/search'
import { selectTicketListTitle } from 'selectors/ticket_list'
import {
  selectSelectedPivotId,
  selectSelectedRangeEndId,
} from 'selectors/ticket_list/base'

import bootstrap from 'util/bootstrap'
import { toQueryString } from 'util/params'
import {
  constructMailboxLessSearchQueryId,
  constructSearchQueryString,
} from 'util/search'
import { batchActions } from 'util/redux'
import { debounce } from 'util/functions'
import GA from 'util/googleAnalytics'
import storage from 'util/storage'

import metrics from 'util/metrics'
import { V2_VERIFICATION_PAGE } from 'subapps/onboarding/pages'
import { ONBOARDING_COMPLETE_SUCCESS } from 'subapps/onboarding/types'
import { AGENT_ACTIVE_OR_ARCHIVED_DATA_TABLE_QUERY_ID } from 'ducks/agents/constants'
import doRedirectToBilling from 'subapps/settings/actions/doRedirectToBilling'

// Currently we're not using the ducks/request module to load our
// bootstrap data. This means we dont have access to all our normalizr magic
// This hack allows us to use that magic without using the full module which
// would required a bigger refactor
// Hack start
import {
  agent as agentEntity,
  team as teamEntity,
  channel as channelEntity,
} from 'ducks/entities/schema'
import entitiesActionModule from 'ducks/requests/modules/entitiesActionModule'
import searchesActionModule from 'ducks/requests/modules/searchesActionModule'
// Hack end

import { doToggleRightSidebar } from './doToggleRightSidebar'

export { doToggleRightSidebar }

const bootstrapQuery = `
  query BootstrapQuery {
    access_lists {
      ${accessListFields}
    }
    agents(includeArchived: true) {
      ${agentFields}
    }
    groups {
      ${groupFields}
    }
    billing {
      ${billingFields}
    }
    ${BILLING_PLANS_FIELDS},
    creditCards {
      ${CREDIT_CARD_FIELDS}
    }
    account {
      ${accountFields}
    }
    user {
      ${userFields}
    }
    folders(byType: "All") {
      ${folderFields}
    }
    mailboxes {
      ${mailboxFields}
    }
    labels(perPage: 100) {
      records {
        id
        name
        color
        labelingsCount
      }
    }
    companyProfile {
      ${companyFields}
    }
  }
`

const appBootstrapQuery = `
  query AppBootstrapQuery {
    integrations {
      ${integrationFields}
    }
  }
`

export function doFetchInboxBootstrapData() {
  return (dispatch, getState) => {
    dispatch(doFetchBootstrapData()).then(() => {
      const isInboxBootstrapped = selectIsInboxBootstrapped(getState())
      if (isInboxBootstrapped) return
      dispatch(
        // we need a second dispatch, because we need the app data to be set in the previous dispatch
        batchActions(
          { type: types.FETCHED_INBOX_DATA },
          doFetchTicketSearchCountsForPinnedSearches(),
          doFetchOpenTicketAggregationCountsForEachMailbox(),
          doFetchFolderCounts(),
          doFetchInaccessibleMailboxes()
        )
      )
    })
  }
}

let cooloff = false
const removeCooloff = debounce(callback => {
  cooloff = false
  callback()
}, 3000) // 2 rotations
const setCooloff = callback => {
  cooloff = true
  removeCooloff(callback)
}
export function doRefreshSearches() {
  return dispatch => {
    GA.track('inbox', 'refresh-searches')
    if (cooloff) return
    setCooloff(() => {
      dispatch(doSearchRefreshFinish())
    })
    dispatch(batchActions(doSearchRefreshStart(), doClearAllSearches()))
    dispatch(
      batchActions(
        doFetchTicketSearchCountsForPinnedSearches(null, {
          metricType: 'starredCounts',
          trackMetrics: true,
          isManual: true,
          isAutomatic: false,
        }),
        doFetchOpenTicketAggregationCountsForEachMailbox({
          trackMetrics: true,
          isManual: true,
          isAutomatic: false,
        }),
        doFetchFolderCounts({
          trackMetrics: true,
          isManual: true,
          isAutomatic: false,
        }),
        doLoadSearchTickets()
      )
    )
  }
}

export function doRefreshCounts() {
  return (dispatch, getState) => {
    const state = getState()
    const isAuthenticated = isAuthenticatedSelector(state)
    if (!isAuthenticated) return
    dispatch(
      batchActions(
        doFetchTicketSearchCountsForPinnedSearches(null, {
          metricType: 'starredCounts',
          trackMetrics: true,
          isManual: false,
          isAutomatic: true,
        }),
        doFetchOpenTicketAggregationCountsForEachMailbox({
          trackMetrics: true,
          isManual: false,
          isAutomatic: true,
        }),
        doFetchFolderCounts({
          trackMetrics: true,
          isManual: false,
          isAutomatic: true,
        })
      )
    )
  }
}

export function doFetchReportsBootstrapData() {
  return dispatch => {
    dispatch(
      batchActions(doFetchBootstrapData(), doFetchInaccessibleMailboxes())
    )
  }
}

export function doFetchSettingsBootstrapData() {
  return dispatch => {
    dispatch(
      batchActions(doFetchBootstrapData(), doFetchInaccessibleMailboxes())
    )
  }
}

export function doFetchKbBootstrapData() {
  return dispatch => {
    dispatch(
      batchActions(doFetchBootstrapData(), doFetchInaccessibleMailboxes())
    )
  }
}

function doSetOnboardingCompletedIfRequired() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const isInOnboarding = selectIsInOnboarding(state)
    const isOnboardingCompleted = selectAccountIsOnboardingCompleted(state)
    const hasFullAccess = selectCurrentUserHasFullAccess(state)

    if (!isInOnboarding && !isOnboardingCompleted && hasFullAccess) {
      return grooveAPI
        .post(token, 'v1/account/complete_onboarding', {})
        .then(() => {
          dispatch({
            type: ONBOARDING_COMPLETE_SUCCESS,
          })
        })
    }
    return null
  }
}

const captureAppBootstrapMetrics = () => {
  try {
    const openStates = storage.get('leftSidebarOpenStates')
    if (openStates) {
      Object.keys(openStates).forEach(key => {
        const value = openStates[key]
        metrics.bool(`leftSidebarOpenStates-${key}`, value)
      })
    }
  } catch (e) {
    console.log(e) // eslint-disable-line
  }
}

// Bootstap function for new GQL API, so we can gradually port types over.
export function doFetchAppBootstrapData() {
  return dispatch => {
    dispatch(
      doGraphqlRequest(
        types.BOOTSTRAP_APP_DATA,
        appBootstrapQuery,
        {},
        { app: true }
      )
    )
  }
}

const handleExpireAccountFailures = error => (dispatch, getState) => {
  const { data } = error
  // If the user needs to confirm their email address
  if (data && data.user && !data.user.fullAccess) {
    dispatch({ type: V2_VERIFICATION_PAGE })
    return
  }

  // /v1/billing/account and /v1/me skips disabled account check
  // This means these endpoints "should" still return data even when
  // the account has expired. Data from both of these endpoints are
  // required for the /settings/billing page to work
  if (
    !data ||
    !data.billing ||
    !data.user ||
    !['expired', 'closed'].includes(data.billing.state)
  )
    return
  dispatch(
    batchActions(
      dispatch({
        type: APP_BILLING_EXPIRED,
        data,
      }),
      doMarkFetchingStatus('fetchAppData', false)
    )
  )
  // Redirect the person to the billing page
  const state = getState()
  if (selectCurrentPage(state) !== pages.SETTINGS_BILLING_PAGE) {
    dispatch(doRedirectToBilling({ withRedirect: true }))
  }
}

export function doFetchBootstrapData() {
  return (dispatch, getState) => {
    const state = getState()
    const isBootstrapped = isBoostrappedSelector(state)
    const isFetchingAppData = fetchingStatusesSelector(state).fetchAppData
    if (isBootstrapped || isFetchingAppData) return Promise.resolve()
    dispatch(doMarkFetchingStatus('fetchAppData', true))
    const token = oauthTokenSelector(state)

    dispatch(doFetchAppBootstrapData())

    return graphql(token, bootstrapQuery)
      .then(res => {
        const data = res.json.data
        dispatch(
          batchActions(
            doUpdateAppData(data),
            doMarkFetchingStatus('fetchAppData', false),
            doSetOnboardingCompletedIfRequired(data),
            doBootstrapWidgetsAndSettings()
          )
        )
        bootstrap.done()
        // for now, only us
        if (data.account.subdomain === 'groove') {
          captureAppBootstrapMetrics(data)
        }
      })
      .catch(error => {
        dispatch(handleExpireAccountFailures(error))
      })
  }
}

const bootstrapSchema = {
  agents: [agentEntity],
  groups: [teamEntity],
  mailboxes: [channelEntity],
}

export function doUpdateAppData(payload, options = {}) {
  return (dispatch, getState) => {
    const state = getState()

    const host = window.location.host
    if (
      !options.preventRedirect &&
      (host.match('^beta.groovehq.com') || host.match('^app.groovehq.com'))
    ) {
      const token = oauthTokenSelector(state)
      const subdomain = payload.account.subdomain
      const destination = window.location.pathname
      localStorage.clear()
      window.location.href = `https://${subdomain}.groovehq.com/auth/callback?access_token=${token}&destination=${encodeURIComponent(
        destination
      )}`
    }

    // Decorate the action payload with some extra bits of (app) store state
    // that non-app reducers dont have access to.
    const path = currentPathSelector(state)
    const queryParams = selectQueryParams(state)

    const latestQueryId = selectCurrentTicketSearchQueryId(getState())

    let successAction = {
      type: types.UPDATE_APP_DATA,
      data: {
        accessLists: payload.access_lists,
        account: payload.account,
        currentUser: payload.user,
        mailboxes: payload.mailboxes,
        folders: payload.folders,
        agents: payload.agents,
        groups: payload.groups,
        embeddableCards: payload.embeddableCards,
        labels: payload.labels && payload.labels.records,
        billing: payload.billing,
        creditCards: payload.creditCards,
        plans: payload.plans,
        path,
        queryParams,
        latestQueryId,
        companyProfile: payload.companyProfile,
      },
    }
    successAction = entitiesActionModule(
      'SUCCESS',
      successAction,
      successAction.data,
      {
        normalizationSchema: bootstrapSchema,
      }
    )

    successAction = searchesActionModule(
      'SUCCESS',
      successAction,
      successAction.data.agents,
      {
        searches: {
          extractPagination: nodes => {
            return {
              type: 'agent',
              nodes,
              startCursor: 1,
              endCursor: 1,
              hasNextPage: false,
              hasPreviousPage: false,
              totalCount: nodes?.length || 0,
              totalPageCount: 1,
            }
          },
          cursor: 'all',
          queryId: AGENT_ACTIVE_OR_ARCHIVED_DATA_TABLE_QUERY_ID,
        },
      }
    )
    return dispatch(successAction)
  }
}

export function doFetchFolderCounts(options) {
  return (dispatch, getState) => {
    const state = getState()
    const searches = selectAllFolderQueries(state)
    return dispatch(doFetchTicketSearchCountsFor(searches, options))
  }
}

export function doMarkUpdateAvailable(isAvailable) {
  return { type: types.MARK_UPDATE_AVAILABLE, data: { available: isAvailable } }
}

// Deprecated. Use Rudy (RFR) Links. ProTip: If you cant use a Link, and need
// to dispatch an action, the correct way would be to dispatch the associated
// _PAGE action
function doNavigate(path, params = null) {
  // eslint-disable-next-line
  console.warn(
    'WARN: doNavigate is deprecated. Use Links or dispatch a route action.'
  )
  let url = path
  if (params) {
    const query = toQueryString(params)
    if (query !== '') url = `${path}?${query}`
  }

  // RFR workaround - we dip into the RFR client API to ensure our history is
  // tracked correctly.
  push(url) // this will trigger a route action automagically...

  // While deprecated, this function still needs to return an action...
  return {
    type: types.DEPRECATED_CHANGE_PATH,
    data: {
      path,
    },
  }
}

export const doSaveReturnPath = path => {
  return {
    type: types.SAVE_RETURN_PATH,
    data: {
      path,
    },
  }
}

export const doClearReturnPath = () => {
  return {
    type: types.CLEAR_RETURN_PATH,
  }
}

export const doNavigateToReturnPath = (path = null) => (dispatch, getState) => {
  const returnPath = selectReturnPath(getState())
  return dispatch(
    batchActions(doNavigate(path || returnPath), doClearReturnPath())
  )
}

export function doToggleNavigation() {
  return (dispatch, getState) => {
    const visible = getState().app.navigationVisible
    dispatch(doChangeNavigationVisibility(!visible))
  }
}

export function doToggleNavSection(section, leaveOpen = false) {
  return dispatch => {
    dispatch({
      type: types.TOGGLE_NAV_SECTION,
      data: {
        section,
        leaveOpen,
      },
    })
    dispatch(doFetchTicketAggregationCountsForUnloadedNavigationSections())
  }
}

export function doChangeNavigationVisibility(visible) {
  return {
    type: types.CHANGE_NAVIGATION_VISIBILITY,
    data: { visible },
    tracking: { event: visible ? 'opened-navigation' : 'closed-navigation' },
  }
}

export function doChangeMailboxSwitcherVisibility({ visible }) {
  return {
    type: types.CHANGE_MAILBOX_SWITCHER_VISIBILITY,
    data: { visible },
  }
}

/* @joelserino - 12/19/17
 ** This needs to be moved to a ticketing init() -
 ** probably somewhere in ./components/App/DesktopVioew/Layout/index.jsx
 */
export function doMarkFetchingStatus(action, newStatus) {
  return {
    type: types.MARK_FETCHING_STATUS,
    data: {
      action,
      status: newStatus,
    },
  }
}

export function doToggleTicketSelection(id) {
  return {
    type: types.TOGGLE_TICKET_SELECTION,
    data: { id },
  }
}

export function doRangedTicketSelection(id, tickets = []) {
  return (dispatch, getState) => {
    const state = getState()
    const sortedMailboxTicketIds = tickets.map(t => t.id)
    const pivotId = selectSelectedPivotId(state)
    const rangeEndId = selectSelectedRangeEndId(state)

    return dispatch({
      type: types.RANGED_TICKET_SELECTION,
      data: { id, pivotId, rangeEndId, sortedMailboxTicketIds },
    })
  }
}

export function doSelectTicket(id) {
  return {
    type: types.SELECT_TICKET,
    data: { id },
  }
}

export function doDeselectTicket(id) {
  return {
    type: types.DESELECT_TICKET,
    data: { id },
  }
}

export function doDeselectAllTickets() {
  return {
    type: types.DESELECT_ALL_TICKETS,
  }
}

function doSelectManyTickets(ticketIds) {
  return {
    type: types.SELECT_TICKETS,
    data: { ticketIds },
  }
}

export function doSelectAllTickets() {
  return (dispatch, getState) => {
    const state = getState()
    const ticketIds = selectCurrentTicketSearchResultTicketIds(state) || []
    return dispatch(doSelectManyTickets(ticketIds))
  }
}

export function doSetTicketsCurtainVisibility(visible) {
  return {
    type: types.SET_TICKETS_CURTAIN_VISIBILITY,
    data: { visible },
  }
}

export function doStartEditingTicketTitle(ticketId, currentTitle) {
  return {
    type: types.TICKET_TITLE_EDITING_START,
    data: { currentTitle, ticketId },
  }
}

export function doStopEditingTicketTitle(ticketId, currentTitle) {
  return {
    type: types.TICKET_TITLE_EDITING_STOP,
    data: { currentTitle, ticketId }, // informational
  }
}

export function doTicketTitleDraftChanged(title) {
  return {
    type: types.TICKET_TITLE_DRAFT_CHANGED,
    data: { title },
  }
}

export function doQueueEditorToolbarMessage(messageType, action, timeout) {
  return {
    type: types.QUEUE_EDITOR_TOOLBAR_MESSAGE,
    data: {
      messageType,
      action,
      timeout,
    },
  }
}

export function doProcessNextEditorToolbarMessage() {
  return {
    type: types.PROCESS_NEXT_EDITOR_TOOLBAR_MESSAGE,
  }
}

export function doClearEditorToolbarMessages() {
  return {
    type: types.CLEAR_EDITOR_TOOLBAR_MESSAGES,
  }
}

export function doSetJustClosedKeyboard() {
  return dispatch => {
    dispatch({
      type: types.SET_JUST_CLOSED_KEYBOARD,
      data: { state: true },
    })

    setTimeout(
      () =>
        dispatch({
          type: types.SET_JUST_CLOSED_KEYBOARD,
          data: { state: false },
        }),
      1000
    )
  }
}

export function doClearFetchedStatuses(regex) {
  return {
    type: types.CLEAR_FETCHED_STATUSES,
    data: {
      regex,
    },
  }
}

export const doUpdateAppDeviceType = (deviceType = {}) => {
  return {
    type: types.UPDATE_APP_DEVICE_TYPE,
    data: {
      ...deviceType,
    },
  }
}

export function doUpdateAccessList(accessList) {
  return {
    type: types.UPDATE_ACCESS_LIST,
    data: accessList,
  }
}

export function doRemoveAccessList(mailboxId) {
  return {
    type: types.REMOVE_ACCESS_LIST,
    data: mailboxId,
  }
}

export function doListTopChanged(scrolledToTop) {
  return {
    type: types.LIST_SCROLL_TOP_CHANGED,
    data: { isTicketListScrolledToTop: scrolledToTop },
  }
}

export function doKeyChange(change = {}) {
  return {
    type: types.KEY_PRESSED_CHANGE,
    data: {
      change,
    },
  }
}

export function doStoreTicketListBeforeSearch() {
  return {
    type: types.STORE_TICKET_LIST_BEFORE_SEARCH,
  }
}

export function doRenderTicketListBeforeSearch() {
  return (dispatch, getState) => {
    const state = getState()
    const ticketListBeforeSearch = selectTicketListBeforeSearch(state)
    const { type, id } = ticketListBeforeSearch

    if (type === 'SEARCH') {
      return dispatch(doOpenSearchPage(ticketListBeforeSearch.id))
    }
    if (type === 'FOLDER') {
      return dispatch(doOpenFolderPage(id))
    }

    return false
  }
}

export function doToggleSearchPinned() {
  return (dispatch, getState) => {
    const state = getState()
    const pinnedSearch = selectCurrentSearchPin(state)

    if (pinnedSearch) return dispatch(doUnpinSearch(pinnedSearch.id))

    const searchQueryObject = selectCurrentTicketSearchQueryObject(state)
    const title = selectTicketListTitle(state)

    return dispatch(doPinSearch(title, searchQueryObject))
  }
}

export function doPinSearch(label, queryObject) {
  return (dispatch, getState) => {
    const state = getState()
    const statuses = fetchingStatusesSelector(state)
    if (statuses.pinSearch) return null

    const valueMap = selectTicketSearchOperatorValueMap(state)
    const query = constructMailboxLessSearchQueryId(queryObject, valueMap)

    dispatch({ type: types.PIN_SEARCH_REQUEST, data: { label, query } })
    const token = oauthTokenSelector(state)

    const mutation = `
      mutation PinSearch($label: String, $query: String) {
        pinSearch(
          label: $label,
          query: $query,
        ) {
          success
          pinned_search {
            id
            label
            query
            position
          }
        }
      }
    `
    const variables = { label, query }

    return graphql(token, mutation, variables).then(res => {
      const data = res.json.data.pinSearch
      if (data.success) {
        dispatch({
          type: types.PIN_SEARCH_SUCCESS,
          data: data.pinned_search,
        })
      } else {
        dispatch({
          type: types.PIN_SEARCH_FAILURE,
          data: data.pinned_search,
        })
      }
    })
  }
}

export function doUnpinSearch(id) {
  return (dispatch, getState) => {
    const state = getState()
    dispatch({ type: types.UNPIN_SEARCH_REQUEST, data: { id } })
    const token = oauthTokenSelector(state)

    const mutation = `
      mutation UnpinSearch($id: String) {
        unpinSearch(
          id: $id,
        ) {
          success
        }
      }
    `
    const variables = { id }

    return graphql(token, mutation, variables).then(res => {
      const data = res.json.data.unpinSearch
      if (data.success) {
        dispatch({
          type: types.UNPIN_SEARCH_SUCCESS,
          data: { id },
        })
      } else {
        dispatch({
          type: types.UNPIN_SEARCH_FAILURE,
          data: { id },
        })
      }
    })
  }
}

export function doUpdateAgentMention(mention) {
  return {
    type: types.UPDATE_AGENT_MENTION,
    data: mention,
  }
}

export function doUpdateMentions(agentId) {
  return {
    type: types.UPDATE_MENTIONS,
    data: agentId,
  }
}

export function doUpdateSelectedMentionSelectorAgentId(agentId) {
  return {
    type: types.UPDATE_SELECTED_MENTION_SELECTOR_AGENT_ID,
    data: agentId,
  }
}

export function doUpdateSuppressMentionSelector(bool) {
  return {
    type: types.UPDATE_SUPPRESS_MENTION_SELECTOR,
    data: bool,
  }
}

export function doNavigateToPinnedSearchForNumKey(event) {
  return (dispatch, getState) => {
    const pinnedSearches = selectedPinnedSearchesList(getState())
    const { key } = event
    const keyOrTen = key === '0' ? 9 : key - 1
    const pinnedSearch = pinnedSearches.items[keyOrTen]
    if (!pinnedSearch || !pinnedSearch.search) return false
    const term = constructSearchQueryString(pinnedSearch.search)
    if (!term) return false
    return dispatch(doOpenSearchPage(term))
  }
}

export function doMailboxSearchInput(term) {
  return {
    type: types.MAILBOX_SEARCH_INPUT,
    data: { term },
  }
}

export function doToggleLeftSidebar() {
  return {
    type: types.TOGGLE_LEFT_SIDEBAR,
  }
}

export function doSetLeftSidebarOpenState(isOpen) {
  return {
    type: types.SET_LEFT_SIDEBAR_OPEN_STATE,
    data: { isOpen },
  }
}

export function doNoop() {
  return {
    type: types.NOOP,
  }
}

export function doSearchRefreshStart() {
  return {
    type: types.SEARCH_REFRESH_START,
  }
}

export function doSearchRefreshFinish() {
  return {
    type: types.SEARCH_REFRESH_FINISH,
  }
}

export function doClearAllSearches() {
  return {
    type: types.SEARCH_CLEAR_ALL,
  }
}

export function doUpdateLastTicketListScrollerScrollTop(scrollTop) {
  return (dispatch, getState) => {
    const state = getState()
    const currentTicketList = selectCurrentTicketList(state)
    dispatch({
      type: types.UPDATE_LAST_TICKET_LIST_SCROLLER_SCROLL_STATE,
      payload: {
        scrollTop,
        searchQueryId: currentTicketList.id,
      },
    })
  }
}

export function doTogglePinExpandedReplyFormHeader() {
  return {
    type: types.TOGGLE_PIN_EXPANDED_REPLY_FORM_HEADER,
  }
}

export function doTogglePrintMode(printMode) {
  return {
    type: types.TOGGLE_PRINT_MODE,
    payload: { printMode },
  }
}

window.runNoop = () => {
  app.store.dispatch(doNoop())
}
